// Studio Workspace A — shows management: create a show, list my shows, manage each
// show's standing cohost roster (add by username / remove). Rendered in the
// production zone of both chromes: /creator/shows and /admin/shows.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import {
  addShowMemberById,
  createShow,
  listShowMembers,
  listShows,
  removeShowMember,
  searchProfiles,
  updateShowTicker,
  uploadShowLogo,
  type ShowSummary,
} from '@/features/live/showActions';
import { TickerEditor } from '@/features/live/TickerEditor';
import { useAuth } from '@/lib/auth/useAuth';

export function ShowsManager() {
  const { session } = useAuth();
  const ownerId = session?.user.id ?? '';
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const shows = useQuery({
    queryKey: ['shows', ownerId],
    enabled: Boolean(ownerId),
    queryFn: () => listShows(ownerId),
  });
  const create = useMutation({
    mutationFn: () => createShow(name, ownerId),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['shows', ownerId] });
    },
  });

  return (
    <div className="space-y-8 p-6">
      <h1 className="font-serif text-2xl text-video-text">Shows</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
        className="flex max-w-md gap-2"
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder="New show name"
          className="min-w-0 flex-1 rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
        <button
          type="submit"
          disabled={!name.trim() || create.isPending}
          className="rounded-md bg-video-text px-4 py-2 font-sans text-sm font-medium text-video-bg disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create show'}
        </button>
      </form>
      {create.isError && (
        <p className="font-sans text-xs text-oxblood-live">
          {create.error instanceof Error ? create.error.message : 'Could not create the show'}
        </p>
      )}

      {shows.isLoading ? (
        <p className="font-sans text-sm text-video-text-faint">Loading…</p>
      ) : (shows.data ?? []).length === 0 ? (
        <p className="font-sans text-sm text-video-text-faint">No shows yet.</p>
      ) : (
        <ul className="space-y-6">
          {shows.data!.map((show) => (
            <li key={show.id}>
              <ShowRoster show={show} ownerId={ownerId} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ShowRoster({ show, ownerId }: { show: ShowSummary; ownerId: string }) {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const members = useQuery({
    queryKey: ['show-members', show.id],
    queryFn: () => listShowMembers(show.id),
  });
  const search = useQuery({
    queryKey: ['profile-search', trimmed],
    queryFn: () => searchProfiles(trimmed),
    enabled: trimmed.length >= 2,
  });
  const add = useMutation({
    mutationFn: (memberId: string) => addShowMemberById(show.id, memberId, ownerId),
    onSuccess: () => {
      setQuery('');
      void qc.invalidateQueries({ queryKey: ['show-members', show.id] });
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => removeShowMember(show.id, memberId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['show-members', show.id] }),
  });
  const updateTicker = useMutation({
    mutationFn: (lines: string[]) => updateShowTicker(show.id, lines),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shows', ownerId] }),
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadLogo = useMutation({
    mutationFn: (file: File) => uploadShowLogo(show.id, file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shows', ownerId] }),
  });

  const memberIds = new Set((members.data ?? []).map((m) => m.memberId));
  const matches = (search.data ?? []).filter((p) => p.id !== ownerId && !memberIds.has(p.id));

  return (
    <div className="space-y-3 rounded-lg border border-video-line p-4">
      <h2 className="font-serif text-lg text-video-text">{show.name}</h2>
      <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
        Standing cohosts
      </p>
      <div className="relative max-w-md">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members by name or @username"
          className="w-full rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
        {trimmed.length >= 2 && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-video-line bg-video-panel shadow-lg">
            {search.isLoading ? (
              <p className="px-3 py-2 font-sans text-xs text-video-text-faint">Searching…</p>
            ) : matches.length === 0 ? (
              <p className="px-3 py-2 font-sans text-xs text-video-text-faint">No matches.</p>
            ) : (
              <ul>
                {matches.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => add.mutate(p.id)}
                      disabled={add.isPending}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-video-card disabled:opacity-50"
                    >
                      {p.avatarUrl ? (
                        <img
                          src={p.avatarUrl}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover"
                        />
                      ) : (
                        <span className="h-6 w-6 shrink-0 rounded-full bg-video-card" />
                      )}
                      <span className="min-w-0 flex-1 truncate font-sans text-sm text-video-text">
                        {p.displayName ?? p.username ?? 'Member'}
                        {p.username ? (
                          <span className="text-video-text-faint"> @{p.username}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {add.isError && (
        <p className="font-sans text-xs text-oxblood-live">
          {add.error instanceof Error ? add.error.message : 'Could not add that member'}
        </p>
      )}
      <ul className="space-y-1">
        {(members.data ?? []).map((m) => (
          <li key={m.memberId} className="flex items-center gap-2 font-sans text-sm">
            <span className="min-w-0 flex-1 truncate text-video-text">
              {m.displayName}
              {m.username ? <span className="text-video-text-faint"> @{m.username}</span> : null}
            </span>
            {m.status === 'pending' && (
              <span className="shrink-0 rounded-sm bg-oxblood-live/20 px-1.5 py-0.5 font-sans text-[10px] font-medium uppercase tracking-[0.1em] text-oxblood-live">
                Pending
              </span>
            )}
            <button
              type="button"
              onClick={() => remove.mutate(m.memberId)}
              className="font-sans text-xs text-video-text-faint hover:text-oxblood-live"
            >
              Remove
            </button>
          </li>
        ))}
        {members.data?.length === 0 && (
          <li className="font-sans text-xs text-video-text-faint">
            No standing cohosts — add a member by username; they can publish in any broadcast of
            this show without a per-room invite.
          </li>
        )}
      </ul>

      <div className="space-y-2 border-t border-video-line pt-3">
        <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
          Show logo (seeds each broadcast)
        </p>
        <div className="flex items-center gap-3">
          {show.logoUrl ? (
            <img src={show.logoUrl} alt="" className="h-12 w-auto max-w-[8rem] object-contain" />
          ) : (
            <span className="font-sans text-xs text-video-text-faint">
              No logo — the wordmark shows.
            </span>
          )}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploadLogo.isPending}
            className="rounded-md border border-video-line px-3 py-1.5 font-sans text-xs text-video-text-muted hover:text-video-text disabled:opacity-50"
          >
            {uploadLogo.isPending ? 'Uploading…' : 'Change logo'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadLogo.mutate(f);
              e.target.value = '';
            }}
          />
        </div>
        {uploadLogo.isError && (
          <p className="font-sans text-xs text-oxblood-live">
            {uploadLogo.error instanceof Error ? uploadLogo.error.message : 'Upload failed'}
          </p>
        )}
        <p className="font-sans text-xs text-video-text-faint">PNG, WEBP, or SVG — up to 2 MB.</p>
      </div>

      <div className="space-y-2 border-t border-video-line pt-3">
        <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
          Ticker template (seeds each broadcast)
        </p>
        <TickerEditor
          initial={show.tickerLines}
          onSave={(lines) => updateTicker.mutate(lines)}
          saving={updateTicker.isPending}
        />
      </div>
    </div>
  );
}
